import { useEffect, useMemo, useState } from "react";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { ComplianceUserItem } from "../../models/interfaces/reports/report-dynamic-compliance-data";

const COMPONENT_CLASS = "c-report-dynamic-grid";

interface ReportDynamicGridProps {
    users: ComplianceUserItem[];
    pageSize?: number;
}

const ReportDynamicGrid: React.FC<ReportDynamicGridProps> = ({
    users,
    pageSize = 12,
}) => {

    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [users]);

    /* Pagination */
    const totalPages = Math.ceil(users.length / pageSize);

    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return users.slice(start, start + pageSize);
    }, [users, currentPage, pageSize]);

    const getVisiblePages = () => {
        const pages: (number | "...")[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        const start = Math.max(2, currentPage - 2);
        const end = Math.min(totalPages - 1, currentPage + 2);

        pages.push(1);

        if (start > 2) pages.push("...");

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < totalPages - 1) pages.push("...");

        pages.push(totalPages);

        return pages;
    };

    return (
        <div className={COMPONENT_CLASS}>

            {/* GRID */}
            <div className={`${COMPONENT_CLASS}__grid`}>
                {paginatedUsers.map((user, index) => (
                    <div key={index} className={`${COMPONENT_CLASS}__item`}>
                        <a
                            href={`${window.location.origin}/users/${user.id}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {user.displayText}
                        </a>
                    </div>
                ))}
            </div>

            {/* PAGINATION */}
            <div className={`${COMPONENT_CLASS}__pagination`}>

                <button
                    className={`${COMPONENT_CLASS}__page-btn`}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                >
                    <ChevronLeftIcon />
                </button>

                {getVisiblePages().map((page, i) => {
                    if (page === "...") {
                        return <span key={i} className={`${COMPONENT_CLASS}__ellipsis`}>...</span>;
                    }

                    return (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`${COMPONENT_CLASS}__page-btn ${page === currentPage
                                ? `${COMPONENT_CLASS}__page-btn--active`
                                : ""
                                }`}
                        >
                            {page}
                        </button>
                    );
                })}

                <button
                    className={`${COMPONENT_CLASS}__page-btn`}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                >
                    <ChevronRightIcon />
                </button>

            </div>
        </div>
    );
};

export default ReportDynamicGrid;